import React from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { ZijMenu, type ZijMenuItem } from './ZijMenu';
import api from '../services/api';
import { employerCockpitApi, ACTIEPUNTEN_KEY } from '../services/employerCockpitApi';
import {
  FileText,
  Search,
  Star,
  FileCheck,
  DollarSign,
  Flag,
  Activity,
  CalendarDays,
  Siren,
  Wallet,
  Thermometer,
  UserPlus,
  Users,
  LayoutGrid,
  HeartPulse,
} from 'lucide-react';

export const SIGNALEN_KEY = ['employer-dashboard-signalen'];

/**
 * Hoofdmenu Dashboard (Onderdeel 50, update B): exact vier banen. De oude
 * cockpit-items (trajecten, facturen, verslagen, gebruikers, …) staan onder
 * de andere hoofdmenu's in de kopbalk.
 */
export const DASHBOARD_PAD = '/employer/dashboard';
/**
 * Professional zoeken (besluit 9): uitgebreid zoeken, resultaten, de kaart en de
 * vacature-workspace hangen hieronder. De oude adressen (/employer/search-professionals,
 * /employer/search-results, /employer/map-search, /employer/vacancies/*) verwijzen door (App.tsx).
 */
export const ZOEKEN_PAD = `${DASHBOARD_PAD}/zoeken`;
export const SIDEBAR_ITEMS: Array<{
  key: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Teamleden zien het item met minstens één van deze permissies; leeg = iedereen. */
  permissionKeys?: string[];
  /** Ook actief op deze paden (onderliggende schermen). */
  ookActief?: string[];
}> = [
  { key: 'actiepunten', href: `${DASHBOARD_PAD}/actiepunten`, icon: Siren, ookActief: ['/employer/arbo-pot'] },
  { key: 'signalen', href: `${DASHBOARD_PAD}/signalen`, icon: LayoutGrid },
  // Eigen ingang naar het verzuimcijfer (staat ook onder Data › Verzuim)
  { key: 'ziekteverzuim', href: '/employer/data/verzuim', icon: HeartPulse },
  { key: 'byop', href: '/employer/byop', icon: UserPlus, permissionKeys: ['samenwerkingen'] },
  {
    key: 'professionalZoeken',
    href: ZOEKEN_PAD,
    icon: Search,
    permissionKeys: ['professional_zoeken', 'geplaatste_opdrachten'],
  },
];

type MenuItem = { key: string; href: string; icon: React.ComponentType<{ className?: string }>; permissionKey?: string };

/**
 * Hoofdmenu Trajecten: onder /employer/trajecten/* toont de linkerkolom het
 * eigen submenu van dat hoofdmenu i.p.v. de Dashboard-items.
 */
export const TRAJECTEN_PAD = '/employer/trajecten';
export const TRAJECTEN_ITEMS: MenuItem[] = [
  { key: 'trajectenAgenda', href: `${TRAJECTEN_PAD}/agenda`, icon: CalendarDays, permissionKey: 'trajecten' },
  { key: 'trajectenLopend', href: `${TRAJECTEN_PAD}/lopend`, icon: Activity, permissionKey: 'trajecten' },
  { key: 'trajectenProfessionals', href: `${TRAJECTEN_PAD}/professionals`, icon: Users, permissionKey: 'samenwerkingen' },
  { key: 'trajectenMijlpalen', href: `${TRAJECTEN_PAD}/mijlpalen`, icon: Flag, permissionKey: 'facturen' },
  { key: 'trajectenFacturen', href: `${TRAJECTEN_PAD}/facturen`, icon: DollarSign, permissionKey: 'facturen' },
  { key: 'trajectenGespreksverslagen', href: `${TRAJECTEN_PAD}/gespreksverslagen`, icon: FileText, permissionKey: 'gespreksverslagen' },
  { key: 'trajectenJaarverslagen', href: `${TRAJECTEN_PAD}/jaarverslagen`, icon: FileCheck, permissionKey: 'jaarverslagen' },
];

/**
 * Hoofdmenu Data (Menu 4): vijf vaste submappen onder /employer/data/*, met
 * een universele datumfilter die alle vijf voedt.
 */
export const DATA_PAD = '/employer/data';
export const DATA_ITEMS: MenuItem[] = [
  { key: 'dataVerzuim', href: `${DATA_PAD}/verzuim`, icon: Activity },
  { key: 'dataCrisis', href: `${DATA_PAD}/crisis`, icon: Siren },
  { key: 'dataBudgetten', href: `${DATA_PAD}/budgetten`, icon: Wallet, permissionKey: 'facturen' },
  { key: 'dataCultuur', href: `${DATA_PAD}/cultuur`, icon: Thermometer },
  { key: 'dataPool', href: `${DATA_PAD}/pool`, icon: Star },
];

const opPad = (pathname: string, pad: string) => pathname === pad || pathname.startsWith(`${pad}/`);

interface EmployerCockpitSidebarProps {
  /** Mobile drawer open state */
  mobileOpen?: boolean;
  /** Close mobile drawer */
  onMobileClose?: () => void;
}

export const EmployerCockpitSidebar: React.FC<EmployerCockpitSidebarProps> = ({ mobileOpen, onMobileClose }) => {
  const { t } = useTranslation('common');
  const location = useLocation();
  const { user } = useAuthStore();
  const pathname = location.pathname;

  const inTrajecten = pathname.startsWith(TRAJECTEN_PAD);
  const inData = pathname.startsWith(DATA_PAD);
  const inDashboard = !inTrajecten && !inData;
  const perms = user?.employer_permissions || [];
  const isLid = user?.role === 'employer_member';

  // Tellers in het menu; dezelfde queries als de pagina's, dus geen dubbele calls.
  const { data: actiepunten } = useQuery({
    queryKey: ACTIEPUNTEN_KEY,
    queryFn: employerCockpitApi.actiepunten,
    enabled: inDashboard,
    refetchInterval: 60_000,
  });
  const { data: signalen } = useQuery<{ ongelezen: number }>({
    queryKey: SIGNALEN_KEY,
    queryFn: async () => (await api.get('/employer/dashboard/signalen')).data.data,
    enabled: inDashboard,
    refetchInterval: 60_000,
  });
  const aantal: Record<string, number | undefined> = {
    actiepunten: actiepunten?.items.length,
    signalen: signalen?.ongelezen,
  };

  let groepItems: ZijMenuItem[];
  if (inDashboard) {
    groepItems = SIDEBAR_ITEMS.filter((item) => !isLid || !item.permissionKeys || item.permissionKeys.some((p) => perms.includes(p))).map(
      (item) => ({
        key: item.key,
        label: t(`employerSidebar.${item.key}`),
        omschrijving: t(`employerSidebar.${item.key}Desc`),
        icon: item.icon,
        href: item.href,
        actief: opPad(pathname, item.href) || (item.ookActief ?? []).some((p) => opPad(pathname, p)),
        aantal: aantal[item.key],
      })
    );
  } else {
    groepItems = (inTrajecten ? TRAJECTEN_ITEMS : DATA_ITEMS)
      .filter((item) => !isLid || !item.permissionKey || perms.includes(item.permissionKey))
      .map((item) => ({ key: item.key, label: t(`employerSidebar.${item.key}`), icon: item.icon, href: item.href, actief: pathname === item.href }));
  }

  return (
    <ZijMenu
      titel={inTrajecten ? t('nav.trajecten') : inData ? t('nav.data') : t('nav.dashboard')}
      ondertitel={inTrajecten ? t('employerSidebar.trajectenSubtitle') : inData ? t('employerSidebar.dataSubtitle') : t('employerSidebar.dashboardSubtitle')}
      groepen={[{ items: groepItems }]}
      mobielOpen={mobileOpen}
      onMobielSluit={onMobileClose}
    />
  );
};
